import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';

/**
 * The cook journal: one entry per time a recipe was cooked, keyed by recipe.
 *
 * Two kinds of entry live here:
 *   - a standalone "I cooked this" logged from the recipe page, with no planned
 *     meal behind it;
 *   - the durable copy of a planned meal's cook note (`mealId` set, id
 *     `mirrorEntryId(mealId)`), written by `cookNoteJournal` (#795).
 *
 * The second kind exists because a cook note used to live ONLY on the planned
 * meal (`PlannedMeal.cookNote`). The meal is the plan, and the plan gets
 * rewritten: Régénérer, Remplacer, Retirer, a drag onto a full cell. On
 * 25 September one tap on Régénérer replaced Thursday's dinner and the note
 * written about it 43 minutes earlier was gone for good, while the household's
 * verdicts — stored per recipe — survived. A note is about the recipe; it is
 * kept with the recipe now.
 *
 * The recipe detail screen derives `timesCooked`, `lastCooked` and the notes by
 * combining these entries with the meal-plan cooked history; an entry whose
 * meal is still cooked in the plan is that same cook, not a second one
 * (`summarizeCookJournal`).
 *
 * Every change is recorded in an outbox (`pending`) and cleared only once
 * Firestore acknowledges it — the same rule as the ratings outbox. Before,
 * a journal write had one debounced chance and a `catch` that logged.
 */

export type CookVerdict = 'again' | 'loved' | 'ok' | 'meh';

export interface CookLogEntry {
  id: string;
  recipeId: string;
  date: string; // ISO date the meal was cooked
  rating: number; // 1-5, 0 when the cook was not rated
  verdict?: CookVerdict;
  note?: string;
  noteBy?: string; // uid of whoever wrote the note
  /** The planned meal this entry mirrors, when it came from one (#795). */
  mealId?: string;
  createdAt: string; // ISO timestamp the entry was recorded
}

/** One write the cloud has not acknowledged yet. */
export interface CookLogPendingOp {
  recipeId: string;
  entryId: string;
  op: 'put' | 'delete';
}

export interface CookLogState {
  // recipeId -> entries (most recent first is NOT guaranteed; selectors sort)
  entries: Record<string, CookLogEntry[]>;
  /** Outbox, keyed by `pendingKey(recipeId, entryId)`. Absent on old persisted state. */
  pending?: Record<string, CookLogPendingOp>;
}

const initialState: CookLogState = {
  entries: {},
  pending: {},
};

/** The journal entry that carries a planned meal's note. Deterministic, so every device writes the same one. */
export const mirrorEntryId = (mealId: string): string => `meal-${mealId}`;

export const pendingKey = (recipeId: string, entryId: string): string => `${recipeId}::${entryId}`;

const markPending = (state: CookLogState, op: CookLogPendingOp): void => {
  if (!state.pending) state.pending = {};
  state.pending[pendingKey(op.recipeId, op.entryId)] = op;
};

const putEntry = (state: CookLogState, entry: CookLogEntry): void => {
  const list = state.entries[entry.recipeId] ?? [];
  const at = list.findIndex(e => e.id === entry.id);
  if (at === -1) list.push(entry);
  else list[at] = entry;
  state.entries[entry.recipeId] = list;
  markPending(state, { recipeId: entry.recipeId, entryId: entry.id, op: 'put' });
};

/**
 * Fold one recipe's cloud entries into the local ones without losing a write
 * the cloud has not acknowledged.
 *
 * Outside the outbox the cloud is the truth: an entry it no longer carries was
 * removed elsewhere. A pending put keeps the local version (the cloud may not
 * have it yet, or may hold the older one); a pending delete keeps it deleted.
 */
export const mergeCookLogEntries = (
  recipeId: string,
  local: CookLogEntry[],
  cloud: CookLogEntry[],
  pending: Record<string, CookLogPendingOp>,
): CookLogEntry[] => {
  const opFor = (id: string) => pending[pendingKey(recipeId, id)]?.op;
  const merged = cloud.filter(e => opFor(e.id) === undefined);
  for (const entry of local) {
    if (opFor(entry.id) === 'put') merged.push(entry);
  }
  return merged;
};

const cookLogSlice = createSlice({
  name: 'cookLog',
  initialState,
  reducers: {
    /**
     * Append a new cook log entry for a recipe.
     */
    addCookLogEntry: (state, action: PayloadAction<CookLogEntry>) => {
      putEntry(state, action.payload);
    },

    /**
     * Write an entry, replacing any entry with the same id. The note mirror
     * uses this, so re-saving a meal's note updates its copy instead of adding
     * a second one.
     */
    upsertCookLogEntry: (state, action: PayloadAction<CookLogEntry>) => {
      putEntry(state, action.payload);
    },

    /**
     * Remove a cook log entry by id.
     */
    removeCookLogEntry: (
      state,
      action: PayloadAction<{ recipeId: string; entryId: string }>
    ) => {
      const { recipeId, entryId } = action.payload;
      const list = state.entries[recipeId];
      if (!list || !list.some(e => e.id === entryId)) return;
      state.entries[recipeId] = list.filter(e => e.id !== entryId);
      markPending(state, { recipeId, entryId, op: 'delete' });
    },

    /**
     * The cloud confirmed this exact operation. A newer op for the same entry
     * (re-queued while the write was in flight) is left alone.
     */
    markCookLogAcked: (state, action: PayloadAction<CookLogPendingOp>) => {
      const { recipeId, entryId, op } = action.payload;
      const key = pendingKey(recipeId, entryId);
      if (state.pending?.[key]?.op === op) delete state.pending[key];
    },

    /**
     * Fold the cloud's journal in (used after cloud hydration). Recipes the
     * cloud does not mention are left as they are.
     */
    hydrateCookLogFromCloud: (
      state,
      action: PayloadAction<Record<string, CookLogEntry[]>>
    ) => {
      const pending = state.pending ?? {};
      for (const [recipeId, cloud] of Object.entries(action.payload)) {
        state.entries[recipeId] = mergeCookLogEntries(
          recipeId,
          state.entries[recipeId] ?? [],
          cloud,
          pending,
        );
      }
    },
  },
});

export const {
  addCookLogEntry,
  upsertCookLogEntry,
  removeCookLogEntry,
  markCookLogAcked,
  hydrateCookLogFromCloud,
} = cookLogSlice.actions;

/**
 * What the disk keeps when storage runs out (the quota ladder's mirrors rung).
 * The journal comes back from Firestore on the next boot, except what the
 * outbox still owes it — that is the only copy, so it stays.
 */
export const trimCookLogForStorage = (state: CookLogState): CookLogState => {
  const pending = state.pending ?? {};
  const entries: Record<string, CookLogEntry[]> = {};
  for (const op of Object.values(pending)) {
    if (op.op !== 'put') continue;
    const entry = state.entries[op.recipeId]?.find(e => e.id === op.entryId);
    if (!entry) continue;
    (entries[op.recipeId] ??= []).push(entry);
  }
  return { entries, pending };
};

/**
 * Download every recipe's cook-log entries from Firestore and merge them into
 * local state. Dispatched from the auth-sync block on sign-in.
 */
export const downloadCookLogFromCloud = createAsyncThunk(
  'cookLog/downloadFromCloud',
  async (userId: string, { getState, dispatch, rejectWithValue }) => {
    try {
      // Lazy-import so the slice doesn't transitively pull in firebaseConfig
      // (react-native Platform) at module load in the test environment.
      const { isFirebaseEnabled } = await import('../services/firebaseConfig');
      if (!isFirebaseEnabled()) {
        throw new Error('Firebase not enabled');
      }
      const { fetchAllCookLogFromCloud } = await import(
        '../services/cookLogSyncService'
      );
      const state = getState() as { auth?: { householdId?: string | null } };
      const householdId = state.auth?.householdId ?? null;
      const cloud = await fetchAllCookLogFromCloud(userId, householdId);
      dispatch(hydrateCookLogFromCloud(cloud));
      return { count: Object.keys(cloud).length };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to download cook log');
    }
  }
);

// ============== SELECTORS ==============

interface RootState {
  cookLog: CookLogState;
}

/**
 * All cook log entries for a recipe, most recent (by cooked date) first.
 */
export const selectRecipeCookLog = (
  state: RootState,
  recipeId: string
): CookLogEntry[] =>
  [...(state.cookLog.entries[recipeId] ?? [])].sort((a, b) =>
    b.date.localeCompare(a.date)
  );

export default cookLogSlice.reducer;
